import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { WindowRef } from '@x/common/browser';
import { ChannelContextService } from '@x/ecommerce-shop/app/channel/services/channel-context.service';
import {
  ChannelState,
  IChannelSelectOption,
} from '@x/ecommerce-shop/app/channel/state/channel.state';
import { TMFAppearance } from '@x/ecommerce-shop/app/core/utils/directives/mat-field-appearance.directive';
import { IShopChannelDetail } from '@x/ecommerce/shop-client';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'x-channel-picker',
  templateUrl: './channel-picker.component.html',
  styleUrls: ['./channel-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-channel-picker' },
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'x-channel-picker-overlay',
        hideSingleSelectionIndicator: true,
      },
    },
  ],
})
export class ChannelPickerComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();

  @Select(ChannelState.activeChannel) activeChannel$: Observable<IShopChannelDetail>;

  channelPickerOptions$ = this.channelContextService.channelPickerOptions$;

  control = new FormControl<IChannelSelectOption | null>(null);

  appearance: TMFAppearance[] = ['no-padding', 'no-fill'];

  @Output() valueChanged = new EventEmitter();

  constructor(
    private router: Router,
    private windowRef: WindowRef,
    private channelContextService: ChannelContextService,
  ) {}

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        tap(async (channel) => {
          if (!channel) return;

          if (channel.url) {
            this.windowRef.document.location.href = channel.url;
            return;
          }

          this.valueChanged.emit();
          await this.router.navigate([`/${channel.code}`], {
            queryParamsHandling: 'preserve',
          });
        }),
      )
      .subscribe();

    this.activeChannel$
      .pipe(
        takeUntil(this._destroy$),
        tap((channel) => {
          if (!channel) return;

          this.control.patchValue(channel, { emitEvent: false });
        }),
      )
      .subscribe();
  }

  compareObjects(o1: { id: number; code: string }, o2: { id: number; code: string }): boolean {
    return o1.id === o2.id;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
